import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle2, MonitorSmartphone, GraduationCap, Car, Star, Quote } from 'lucide-react';
import { api } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [promoSlide, setPromoSlide] = useState(0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [promoBlocks, setPromoBlocks] = useState([]);
  const [scme, setScme] = useState({});
  const [gReviews, setGReviews] = useState({});

  useEffect(() => {
    api.getProducts().then(setProducts).catch(() => {});
    api.getReviews().then(setReviews).catch(() => {});
    api.getContentBlocks('hero').then(setHeroBanners).catch(() => {});
    api.getContentBlocks('promo').then(setPromoBlocks).catch(() => {});
    api.getScmeSettings().then(setScme).catch(() => {});
    api.getGoogleReviewsConfig().then(setGReviews).catch(() => {});
  }, []);

  // Slider automático solo si hay 2+ banners (configurado por datos, no por código)
  useEffect(() => {
    if (heroBanners.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroBanners.length), 6000);
    return () => clearInterval(t);
  }, [heroBanners.length]);

  const featured = products.filter((p) => (p.tags || []).includes('popular')).slice(0, 3);
  const reviewsByMode = reviews.filter((r) => {
    if (gReviews.displayMode === 'internas') return r.source !== 'google';
    if (gReviews.displayMode === 'google') return r.source === 'google';
    return true;
  });
  const shownReviews = [...reviewsByMode].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).slice(0, gReviews.maxVisible || 3);

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !name) { toast.error('Introduce tu nombre y correo'); return; }
    toast.success('¡Gracias por suscribirte al Newsletter!');
    setEmail(''); setName('');
  };

  const s = heroBanners[Math.min(slide, heroBanners.length - 1)];
  const promo = promoBlocks[Math.min(promoSlide, promoBlocks.length - 1)];
  const isSlider = heroBanners.length >= 2;
  const isPromoSlider = promoBlocks.length >= 2;

  return (
    <div>
      {/* Payment band (estilo compacto tipo PayPal) */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <div className="bg-black text-white inline-flex items-center gap-4 pl-5 pr-8 py-4 rounded-sm max-w-full" data-testid="payment-band">
          <span className="italic font-black text-2xl tracking-tight shrink-0" aria-label="PayPal">
            <span className="text-[#009cde]">Pay</span><span className="text-[#7fbfff]">Pal</span>
          </span>
          <span className="text-base md:text-lg font-bold">
            Compra ahora y paga después. <span className="text-xs font-normal text-stone-300 underline cursor-pointer">Más información</span>
          </span>
        </div>
      </div>

      {/* Hero: banner estático o slider según número de banners activos (gestionado desde MATRIZ) */}
      {s && (
        <section className="relative h-[440px] md:h-[520px] overflow-hidden" data-testid="hero-slider">
          <picture key={s.id}>
            {s.imageMobile && <source media="(max-width: 767px)" srcSet={s.imageMobile} />}
            <img src={s.imageDesktop} alt={s.imageAlt || s.title} className="absolute inset-0 w-full h-full object-cover hero-slide-enter" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
            <div className="max-w-xl text-white hero-slide-enter" key={`t-${s.id}`}>
              <h1 className="text-3xl md:text-5xl font-black mb-4 leading-tight">{s.title}</h1>
              <p className="text-base md:text-lg text-stone-100 mb-6">{s.subtitle}</p>
              {s.buttonText && (
                <Link to={s.buttonUrl || '/tienda'} className="inline-block bg-sky-400 hover:bg-sky-500 transition-colors text-white font-bold px-8 py-3 rounded-full text-sm" data-testid="hero-cta">
                  {s.buttonText}
                </Link>
              )}
            </div>
          </div>
          {isSlider && (
            <>
              <button onClick={() => setSlide((slide - 1 + heroBanners.length) % heroBanners.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 transition-colors text-white p-2 rounded-full" data-testid="hero-prev">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={() => setSlide((slide + 1) % heroBanners.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 transition-colors text-white p-2 rounded-full" data-testid="hero-next">
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {heroBanners.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === slide ? 'bg-white' : 'bg-white/40'}`} />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Contador de aprobados (activable desde MATRIZ → Ecosistema SCME) */}
      {scme.approvedCounterEnabled && scme.approvedCounterTotal > 0 && (
        <section className="bg-amber-400" data-testid="approved-counter">
          <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
            <GraduationCap className="w-9 h-9 text-stone-900 shrink-0" />
            <p className="text-stone-900 text-lg md:text-xl">
              <span className="font-black text-2xl md:text-3xl mr-2" data-testid="approved-counter-total">{Number(scme.approvedCounterTotal).toLocaleString('es-ES')}</span>
              alumnos aprobados desde nuestra inauguración
            </p>
          </div>
        </section>
      )}

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-black text-stone-800 text-left mb-2">Tu camino a la independencia</h2>
        <p className="text-stone-500 mb-10">Todo online, sin complicaciones. Así de simple.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: GraduationCap, title: '1. Matricúlate', text: 'Elige tu pack y matricúlate online en 2 minutos.' },
            { icon: MonitorSmartphone, title: '2. Teórico online', text: 'Método esquematizado con 80% de aprobados a la primera.' },
            { icon: Car, title: '3. Clases prácticas', text: 'Reserva tus clases con profesores titulados.' },
            { icon: CheckCircle2, title: '4. ¡Aprobado!', text: 'Consigue tu carnet y tu independencia al volante.' },
          ].map(({ icon: Icon, title, text }, i) => (
            <div key={i} className="bg-white border border-stone-200 p-6 rounded-lg product-card">
              <Icon className="w-9 h-9 text-amber-500 mb-4" />
              <h3 className="font-bold text-stone-800 mb-2">{title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-white border-y border-stone-200 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-stone-800">Los más vendidos</h2>
              <p className="text-stone-500 mt-1">Los packs favoritos de nuestros alumnos.</p>
            </div>
            <Link to="/tienda" className="text-teal-600 font-bold text-sm hover:text-teal-700 transition-colors" data-testid="ver-tienda-link">Ver toda la tienda →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Opiniones de alumnos (módulo activable desde MATRIZ → Opiniones) */}
      {scme.reviewsSectionEnabled !== false && (shownReviews.length > 0 || gReviews.enabled) && (
        <section className="max-w-7xl mx-auto px-4 py-16" data-testid="reviews-section">
          <h2 className="text-2xl md:text-3xl font-black text-stone-800 mb-2">Lo que dicen nuestros alumnos</h2>
          <p className="text-stone-500 mb-8">Opiniones reales de aprobados con Natur Autoescuela.</p>

          {/* Bloque de confianza Google Reviews */}
          {gReviews.enabled && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="google-reviews-block">
              <div className="flex items-center gap-4">
                <svg viewBox="0 0 24 24" className="w-9 h-9 shrink-0"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
                <div>
                  {gReviews.showAverage && (
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-5 h-5 ${n <= Math.round(gReviews.averageRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
                        ))}
                      </div>
                      <span className="font-black text-xl text-stone-800" data-testid="google-reviews-average">{String(gReviews.averageRating || 0).replace('.', ',')}/5</span>
                    </div>
                  )}
                  {gReviews.showTotal && gReviews.totalReviews > 0 && (
                    <p className="text-sm text-stone-500" data-testid="google-reviews-total">+{Number(gReviews.totalReviews).toLocaleString('es-ES')} opiniones verificadas en Google</p>
                  )}
                </div>
              </div>
              {gReviews.profileUrl && (
                <a href={gReviews.profileUrl} target="_blank" rel="noreferrer" className="border border-stone-300 hover:border-stone-500 transition-colors text-stone-700 font-bold text-sm px-6 py-2.5 rounded-full" data-testid="google-reviews-link">
                  Ver opiniones
                </a>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shownReviews.map((r) => (
              <div key={r.id} className="bg-white border border-stone-200 rounded-lg product-card flex flex-col overflow-hidden" data-testid={`review-card-${r.id}`}>
                {scme.reviewVideosEnabled && r.video ? (
                  <video src={r.video} controls playsInline preload="metadata" poster={r.photo || undefined} className="w-full h-44 object-cover bg-black" data-testid={`review-video-${r.id}`} />
                ) : r.photo && (
                  <img src={r.photo} alt={`${r.name} - alumno aprobado`} className="w-full h-44 object-cover" loading="lazy" data-testid={`review-photo-${r.id}`} />
                )}
                <div className="p-6 flex flex-col flex-1">
                <Quote className="w-7 h-7 text-amber-300 mb-3" />
                <p className="text-sm text-stone-600 leading-relaxed flex-1">"{r.text}"</p>
                <div className="flex items-center gap-1 mt-4 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
                  ))}
                </div>
                <p className="text-sm font-bold text-stone-800 flex items-center gap-1.5">{r.name} <span className="font-normal text-stone-400">· {r.city}</span>
                  {r.source === 'google' && (
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" data-testid={`google-badge-${r.id}`}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
                  )}
                </p>
                <p className="text-xs text-teal-600">{r.source === 'google' ? 'Verificada en Google' : r.product}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bloques promocionales: bloque único o slider según datos (gestionado desde MATRIZ) */}
      {promo && (
        <section className="relative max-w-7xl mx-auto px-4 py-16" data-testid="promo-section">
          <div key={promo.id} className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center hero-slide-enter">
            {promo.imageLeft && <img src={promo.imageLeft} alt={promo.imageAlt || promo.title} className="rounded-lg shadow-md w-full h-56 object-cover bg-white" loading="lazy" />}
            <div className="text-center px-4 md:col-span-1">
              <h2 className="text-2xl font-black text-stone-800 mb-3">{promo.title}</h2>
              <p className="text-stone-500 mb-6">{promo.description}</p>
              {promo.buttonText && (
                <Link to={promo.buttonUrl || '/tienda'} className="inline-block bg-sky-400 hover:bg-sky-500 transition-colors text-white font-bold px-8 py-3 rounded text-sm" data-testid="promo-cta">
                  {promo.buttonText}
                </Link>
              )}
            </div>
            {promo.imageRight && <img src={promo.imageRight} alt={promo.imageAlt || promo.title} className="rounded-lg shadow-md w-full h-56 object-cover bg-white" loading="lazy" />}
          </div>
          {isPromoSlider && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button onClick={() => setPromoSlide((promoSlide - 1 + promoBlocks.length) % promoBlocks.length)} className="bg-white border border-stone-300 hover:border-stone-500 transition-colors text-stone-600 p-2 rounded-full" data-testid="promo-prev">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {promoBlocks.map((_, i) => (
                  <button key={i} onClick={() => setPromoSlide(i)} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === promoSlide ? 'bg-stone-700' : 'bg-stone-300'}`} />
                ))}
              </div>
              <button onClick={() => setPromoSlide((promoSlide + 1) % promoBlocks.length)} className="bg-white border border-stone-300 hover:border-stone-500 transition-colors text-stone-600 p-2 rounded-full" data-testid="promo-next">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </section>
      )}

      {/* Newsletter */}
      <section className="bg-[#EFF3EC] py-14">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-black text-stone-700 mb-4">Únete a nuestro Newsletter</h2>
            <p className="text-sm text-stone-500 leading-relaxed">
              Podrá solicitar la baja en cualquier momento a través del procedimiento automatizado disponible o mediante comunicación escrita remitida a los datos de contacto publicados en el Aviso Legal.
            </p>
          </div>
          <form onSubmit={subscribe} className="space-y-3" data-testid="newsletter-form">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (acción requerida)" className="w-full border border-stone-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="newsletter-name" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Su Correo electrónico (acción requerida)" className="w-full border border-stone-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="newsletter-email" />
            <label className="flex items-start gap-2 text-xs text-stone-500">
              <input type="checkbox" className="mt-0.5" />
              <span>Para más información puede visitar nuestras <Link to="/legal/condiciones-generales" className="text-sky-600">Condiciones Generales</Link>, <Link to="/legal/politica-privacidad" className="text-sky-600">Política de Privacidad</Link> y <Link to="/legal/politica-cookies" className="text-sky-600">Política de Cookies</Link>.</span>
            </label>
            <button type="submit" className="w-full bg-sky-400 hover:bg-sky-500 transition-colors text-white font-bold py-3 text-sm tracking-wide" data-testid="newsletter-submit">SUSCRIBIRSE</button>
          </form>
        </div>
      </section>
    </div>
  );
}
